<template>
  <div id="account-security">
    <div class="title-wrap">
      <div class="title-name">账户安全</div>
    </div>
    <p class="intro">
      开启安全校验提高账户安全等级，账户登录、提现（现金和迷你豆）、兑换商城、银行卡改绑、登录工作室需要进行安全校验
    </p>

    <div class="security-panel">
      <div class="setting-card">
        <div class="setting-main">
          <div class="setting-info">
            <div class="setting-title">设置独立密码</div>
            <div class="setting-desc">开启后，敏感操作需先验证独立密码</div>
          </div>
          <div class="setting-actions">
            <div class="switch-block">
              <span class="status-tag" :class="{ 'is-on': independentPwdOn }">{{
                independentPwdOn ? '已开启' : '已关闭'
              }}</span>
              <el-switch
                :key="'sec-pw-' + switchRenderKey + '-' + developerInfo.sec_pw"
                :value="independentPwdOn"
                active-color="#fd9b21"
                inactive-color="#dcdfe6"
                @change="onIndependentPwdSwitch"
              />
            </div>
            <button
              v-if="independentPwdOn"
              type="button"
              class="action-link"
              @click="handleModifyPwd"
            >
              修改独立密码
            </button>
          </div>
        </div>
      </div>

      <div class="setting-card">
        <div class="setting-main">
          <div class="setting-info">
            <div class="setting-title">短信验证码</div>
            <div class="setting-desc">开启后，敏感操作需验证手机短信验证码</div>
          </div>
          <div class="setting-actions">
            <div class="switch-block">
              <span class="status-tag" :class="{ 'is-on': smsOn }">{{
                smsOn ? '已开启' : '已关闭'
              }}</span>
              <el-switch
                :key="
                  'phone-auth-' +
                  switchRenderKey +
                  '-' +
                  developerInfo.phone_auth
                "
                :value="smsOn"
                active-color="#fd9b21"
                inactive-color="#dcdfe6"
                @change="onSmsSwitch"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <el-dialog
      :visible.sync="dialogVisible"
      :title="dialogTitle"
      class="security-dialog"
      width="600px"
      append-to-body
      @closed="onDialogClosed"
    >
      <div class="dialog-body">
        <p v-if="dialogIntro" class="dialog-intro">{{ dialogIntro }}</p>

        <!-- 开启 / 关闭 短信 -->
        <template
          v-if="dialogMode === 'enableSms' || dialogMode === 'disableSms'"
        >
          <div class="send-wrap">
            <div class="field-label">输入手机验证码</div>
            <div class="field-row">
              <el-input
                v-model="smsCode"
                placeholder="请输入验证码"
                maxlength="8"
              />
              <el-button
                type="warning"
                :disabled="smsCodeDisabled"
                @click="sendSmsCode"
                >{{ smsCodeTxt }}</el-button
              >
            </div>
          </div>
        </template>

        <!-- 关闭独立密码：仅验证码 -->
        <template v-else-if="dialogMode === 'disablePwd'">
          <p class="dialog-sub">
            关闭后，您可使用账号密码正常登录，部分敏感操作将不再校验独立密码。
          </p>
          <div class="send-wrap">
            <div class="field-label">输入手机验证码</div>
            <div class="field-row">
              <el-input
                v-model="disablePwdCode"
                placeholder="请输入验证码"
                maxlength="8"
              />
              <el-button
                type="warning"
                :disabled="smsCodeDisabled"
                @click="sendSmsCode"
                >{{ smsCodeTxt }}</el-button
              >
            </div>
          </div>
        </template>

        <!-- 开启独立密码 -->
        <template v-else-if="dialogMode === 'enablePwd'">
          <div class="form-rows">
            <div class="labeled-row">
              <span class="row-label">输入密码</span>
              <el-input
                v-model="enablePwdForm.password"
                type="password"
                show-password
                placeholder="请输入独立密码（8-16位）"
                maxlength="32"
              />
            </div>
            <div class="labeled-row">
              <span class="row-label">再次输入密码</span>
              <el-input
                v-model="enablePwdForm.password2"
                type="password"
                show-password
                placeholder="请再次输入独立密码（8-16位）"
                maxlength="32"
              />
            </div>
            <div class="labeled-row">
              <span class="row-label">手机号</span>
              <div class="readonly-phone">{{ maskedPhone }}</div>
            </div>
            <div class="labeled-row">
              <span class="row-label">验证码</span>
              <div class="field-row flat">
                <el-input
                  v-model="enablePwdForm.code"
                  placeholder="请输入验证码"
                  maxlength="8"
                />
                <el-button
                  type="warning"
                  :disabled="smsCodeDisabled"
                  @click="sendSmsCode"
                  >{{ smsCodeTxt }}</el-button
                >
              </div>
            </div>
          </div>
        </template>

        <!-- 修改独立密码 -->
        <template v-else-if="dialogMode === 'modifyPwd'">
          <p class="dialog-sub highlight">
            您正在进行重要操作，请先验证您的手机号
          </p>
          <div class="form-rows">
            <div class="labeled-row">
              <span class="row-label">输入新密码</span>
              <el-input
                v-model="modifyPwdForm.password"
                type="password"
                show-password
                placeholder="请输入新独立密码"
                maxlength="32"
              />
            </div>
            <div class="labeled-row">
              <span class="row-label">输入手机验证码</span>
              <div class="field-row flat">
                <el-input
                  v-model="modifyPwdForm.code"
                  placeholder="请输入验证码"
                  maxlength="8"
                />
                <el-button
                  type="warning"
                  :disabled="smsCodeDisabled"
                  @click="sendSmsCode"
                  >{{ smsCodeTxt }}</el-button
                >
              </div>
            </div>
          </div>
          <p class="dialog-footer-tip">
            请牢记独立密码，当前暂不支持通过其他方式找回。
          </p>
        </template>

        <div class="bottom-wrap">
          <el-button type="warning" @click="confirmDialog">确认</el-button>
        </div>
      </div>
    </el-dialog>

    <BindPhoneDialog
      :visible.sync="bindPhoneVisible"
      @success="applySecurityResult"
    />
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex';
import { getKfzInfo, getVercode } from '@/api/common';
import BindPhoneDialog from '@/components/BindPhoneDialog.vue';
import { setSecPw, setPhoneAuth, closeSecPw } from '@/api/accountSecurity';

export default {
  name: 'AccountSecurity',
  components: {
    BindPhoneDialog,
  },
  data() {
    return {
      dialogVisible: false,
      bindPhoneVisible: false,
      dialogMode: '',
      smsCode: '',
      disablePwdCode: '',
      enablePwdForm: {
        password: '',
        password2: '',
        code: '',
      },
      modifyPwdForm: {
        password: '',
        code: '',
      },
      smsCodeTxt: '获取验证码',
      smsCodeDisabled: false,
      countDownTimer: null,
      switchRenderKey: 0,
    };
  },
  mounted() {
    this.refreshSecurityState();
  },
  activated() {
    this.refreshSecurityState();
  },
  computed: {
    ...mapState(['developerInfo', 'userInfo']),
    independentPwdOn() {
      return Number(this.developerInfo.sec_pw) === 1;
    },
    smsOn() {
      return Number(this.developerInfo.phone_auth) === 1;
    },
    maskedPhone() {
      const p = this.developerInfo && this.developerInfo.phone;
      return this.maskPhone(p);
    },
    dialogTitle() {
      const map = {
        enableSms: '开启短信验证',
        disableSms: '关闭短信验证码',
        enablePwd: '开启独立密码',
        disablePwd: '关闭独立密码',
        modifyPwd: '修改独立密码',
      };
      return map[this.dialogMode] || '';
    },
    dialogIntro() {
      if (this.dialogMode === 'enableSms') {
        return '开启短信验证后，账户登录、提现（现金和迷你豆）、兑换商城、银行卡改绑、登录工作室需要进行安全校验。';
      }
      if (this.dialogMode === 'enablePwd') {
        return '开启独立密码后，账户登录、提现（现金和迷你豆）、兑换商城、银行卡改绑、登录工作室需要进行安全校验。';
      }
      return '';
    },
  },
  methods: {
    ...mapMutations(['SET_DEVELOPER_INFO', 'SET_ACCOUNT_SECURITY']),
    getUin() {
      return this.userInfo && this.userInfo.uin;
    },
    /** 操作成功后：先本地更新开关，再拉取开发者信息（避免旧数据覆盖新状态） */
    async applySecurityResult(override) {
      if (override && Object.keys(override).length) {
        this.SET_ACCOUNT_SECURITY(override);
        this.switchRenderKey += 1;
        await this.$nextTick();
      }
      try {
        const res = await getKfzInfo();
        if (res && res.data) {
          const data = { ...res.data };
          if (override) {
            if (override.smsOn !== undefined) {
              data.phone_auth = override.smsOn ? 1 : 0;
            }
            if (override.independentPwdOn !== undefined) {
              data.sec_pw = override.independentPwdOn ? 1 : 0;
            }
          }
          this.SET_DEVELOPER_INFO(data);
          this.switchRenderKey += 1;
        }
      } catch (e) {
        console.error(e);
      }
    },
    getSecurityOverrideByMode(mode) {
      const map = {
        enableSms: { smsOn: true },
        disableSms: { smsOn: false },
        enablePwd: { independentPwdOn: true },
        disablePwd: { independentPwdOn: false },
      };
      return map[mode] || null;
    },
    async refreshSecurityState() {
      try {
        const res = await getKfzInfo();
        if (res && res.data) {
          this.SET_DEVELOPER_INFO(res.data);
          this.switchRenderKey += 1;
        }
      } catch (e) {
        console.error(e);
      }
    },
    maskPhone(phone) {
      if (!phone) return '—';
      const s = String(phone);
      if (s.length <= 4) return s;
      return `${s.slice(0, 2)}*******${s.slice(-2)}`;
    },
    onIndependentPwdSwitch(val) {
      if (!this.validatePhoneBound()) return;
      this.openDialog(val ? 'enablePwd' : 'disablePwd');
    },
    onSmsSwitch(val) {
      if (!this.validatePhoneBound()) return;
      this.openDialog(val ? 'enableSms' : 'disableSms');
    },
    handleModifyPwd() {
      if (!this.validatePhoneBound()) return;
      this.openDialog('modifyPwd');
    },
    openDialog(mode) {
      this.resetDialogForms();
      this.dialogMode = mode;
      this.dialogVisible = true;
      this.clearCountDown();
    },
    onDialogClosed() {
      this.dialogMode = '';
      this.resetDialogForms();
      this.clearCountDown();
    },
    resetDialogForms() {
      this.smsCode = '';
      this.disablePwdCode = '';
      this.enablePwdForm = { password: '', password2: '', code: '' };
      this.modifyPwdForm = { password: '', code: '' };
    },
    clearCountDown() {
      if (this.countDownTimer) {
        clearInterval(this.countDownTimer);
        this.countDownTimer = null;
      }
      this.smsCodeDisabled = false;
      this.smsCodeTxt = '获取验证码';
    },
    validatePhoneBound() {
      const phone = this.developerInfo && this.developerInfo.phone;
      if (!phone) {
        this.bindPhoneVisible = true;
        return false;
      }
      return true;
    },
    async sendSmsCode() {
      if (this.smsCodeDisabled) return;
      if (!this.validatePhoneBound()) return;
      try {
        await getVercode({
          phone: this.developerInfo.phone,
          tk: this.userInfo.tk,
        });
        this.$message.success('发送成功');
        this.startCountDown();
      } catch (e) {
        console.error(e);
      }
    },
    startCountDown() {
      this.clearCountDown();
      let time = 60;
      this.smsCodeDisabled = true;
      this.smsCodeTxt = `重新发送${time}S`;
      this.countDownTimer = setInterval(() => {
        time--;
        if (time > 0) {
          this.smsCodeTxt = `重新发送${time}S`;
        } else {
          this.clearCountDown();
        }
      }, 1000);
    },
    async confirmDialog() {
      if (!this.validatePhoneBound()) return;
      if (!this.getUin()) {
        this.$message.error('用户信息异常，请重新登录');
        return;
      }
      try {
        let successMsg = '';
        if (this.dialogMode === 'enableSms') {
          if (!this.smsCode) {
            this.$message.warning('请输入验证码');
            return;
          }
          await setPhoneAuth({
            uin: this.getUin(),
            vercode: this.smsCode,
            flag: 1,
          });
          successMsg = '已开启短信验证';
        } else if (this.dialogMode === 'disableSms') {
          if (!this.smsCode) {
            this.$message.warning('请输入验证码');
            return;
          }
          await setPhoneAuth({
            uin: this.getUin(),
            vercode: this.smsCode,
            flag: 0,
          });
          successMsg = '已关闭短信验证';
        } else if (this.dialogMode === 'enablePwd') {
          const { password, password2, code } = this.enablePwdForm;
          if (!password || !password2) {
            this.$message.warning('请填写密码');
            return;
          }
          if (password !== password2) {
            this.$message.warning('两次输入的密码不一致');
            return;
          }
          if (!code) {
            this.$message.warning('请输入验证码');
            return;
          }
          await setSecPw({
            uin: this.getUin(),
            password,
            vercode: code,
          });
          successMsg = '已开启独立密码';
        } else if (this.dialogMode === 'disablePwd') {
          if (!this.disablePwdCode) {
            this.$message.warning('请输入验证码');
            return;
          }
          await closeSecPw({
            uin: this.getUin(),
            vercode: this.disablePwdCode,
          });
          successMsg = '已关闭独立密码';
        } else if (this.dialogMode === 'modifyPwd') {
          const { password, code } = this.modifyPwdForm;
          if (!password) {
            this.$message.warning('请输入新密码');
            return;
          }
          if (!code) {
            this.$message.warning('请输入验证码');
            return;
          }
          await setSecPw({
            uin: this.getUin(),
            password,
            vercode: code,
          });
          successMsg = '独立密码已修改';
        }
        const override = this.getSecurityOverrideByMode(this.dialogMode);
        this.dialogVisible = false;
        this.$message.success(successMsg);
        await this.applySecurityResult(override);
      } catch (e) {
        console.error(e);
      }
    },
  },
  beforeDestroy() {
    this.clearCountDown();
  },
};
</script>

<style lang="scss" scoped>
$--color-primary: #fd9b21;

#account-security {
  .title-wrap {
    font-size: 24px;
    color: #595959;
    padding-bottom: 24px;
    border-bottom: 1px solid #d9d9d9;
    margin-bottom: 0;
  }

  .title-name {
    line-height: 32px;
  }

  .intro {
    margin: 24px 0 32px;
    padding: 16px 20px;
    max-width: 100%;
    font-size: 14px;
    color: #8c8c8c;
    line-height: 24px;
    background: #fafafa;
    border-radius: 8px;
    border-left: 3px solid $--color-primary;
  }

  .security-panel {
    max-width: 880px;
    margin: 0 auto 0 48px;
    padding-right: 48px;
  }

  .setting-card {
    margin-bottom: 20px;
    padding: 0;
    background: #fff;
    border: 1px solid #ebeef5;
    border-radius: 8px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
    transition: box-shadow 0.2s ease;

    &:hover {
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  .setting-main {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 32px;
    padding: 28px 32px;
    min-height: 88px;
  }

  .setting-info {
    flex: 1;
    min-width: 0;
    padding-top: 2px;
  }

  .setting-title {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
    line-height: 24px;
  }

  .setting-desc {
    margin-top: 10px;
    font-size: 14px;
    color: #909399;
    line-height: 22px;
  }

  .setting-actions {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: center;
    gap: 14px;
    min-width: 148px;
    padding-top: 2px;
  }

  .switch-block {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
  }

  .status-tag {
    display: inline-block;
    min-width: 52px;
    padding: 2px 10px;
    font-size: 12px;
    line-height: 20px;
    text-align: center;
    color: #909399;
    background: #f4f4f5;
    border-radius: 4px;

    &.is-on {
      color: $--color-primary;
      background: rgba(253, 155, 33, 0.12);
    }
  }

  .action-link {
    margin: 0;
    padding: 0;
    border: none;
    background: transparent;
    color: #50a3f1;
    font-size: 14px;
    line-height: 22px;
    cursor: pointer;
    white-space: nowrap;
    transition: color 0.2s ease;

    &:hover {
      color: #3d8ee6;
      text-decoration: underline;
    }
  }

  ::v-deep .el-switch {
    height: 22px;
    line-height: 22px;

    .el-switch__core {
      width: 44px !important;
      height: 22px;
      border-radius: 11px;

      &::after {
        width: 18px;
        height: 18px;
      }
    }
  }
}

.security-dialog {
  ::v-deep .el-dialog__header {
    padding: 24px 24px 12px;
    .el-dialog__title {
      font-size: 18px;
      font-weight: 600;
      color: #303133;
    }
  }

  ::v-deep .el-dialog__body {
    padding: 8px 24px 24px;
  }

  .dialog-body {
    font-size: 14px;
    color: #606266;

    .dialog-intro {
      margin: 0 0 20px;
      line-height: 22px;
      color: #8c8c8c;
    }

    .dialog-sub {
      margin: 0 0 16px;
      line-height: 22px;
      color: #8c8c8c;

      &.highlight {
        color: #606266;
      }
    }

    .dialog-footer-tip {
      margin: 16px 0 0;
      font-size: 12px;
      color: #909399;
      line-height: 20px;
    }

    .field-label {
      width: 130px;
      flex-shrink: 0;
      padding-top: 10px;
    }

    .send-wrap {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      margin-bottom: 8px;
      padding-left: 8px;
    }

    .field-row {
      display: flex;
      align-items: center;
      flex: 1;
      gap: 12px;

      ::v-deep .el-input {
        flex: 1;
        max-width: 280px;
      }

      .el-button {
        height: 40px;
        border-radius: 8px;
        min-width: 120px;
        background-color: $--color-primary;
        border-color: $--color-primary;
      }

      .el-button.is-disabled {
        background: #d9d9d9 !important;
        border-color: #d9d9d9 !important;
      }

      &.flat {
        width: 100%;
        max-width: none;
      }
    }

    .form-rows {
      padding: 0 8px;

      .labeled-row {
        display: flex;
        align-items: center;
        margin-bottom: 20px;

        &:last-child {
          margin-bottom: 8px;
        }

        .row-label {
          width: 120px;
          flex-shrink: 0;
          color: #606266;
          text-align: right;
          padding-right: 16px;
        }

        ::v-deep .el-input {
          flex: 1;
          max-width: 360px;
        }

        .readonly-phone {
          flex: 1;
          max-width: 360px;
          height: 40px;
          line-height: 40px;
          padding: 0 15px;
          background: #f5f7fa;
          border: 1px solid #dcdfe6;
          border-radius: 4px;
          color: #909399;
        }

        .field-row {
          flex: 1;
          max-width: 480px;
        }
      }
    }

    .bottom-wrap {
      text-align: center;
      margin-top: 28px;

      .el-button {
        min-width: 160px;
        height: 40px;
        border-radius: 8px;
        background-color: $--color-primary;
        border-color: $--color-primary;
      }
    }
  }
}
</style>
